import React from 'react';
import {
  Button,
  Flex,
  Group,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { IconDownload, IconInfoCircle } from '@tabler/icons-react';
import { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import DrawerFlowScreenLayout from 'src/UI/drawer-flow/DrawerFlowScreenLayout';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import {
  MeetingNoteTense,
  SectionSplittingLevel,
  TemplateCreationData,
} from 'src/company/templates/types';
import { downloadTemplateJson } from 'src/company/templates/utils/templateExport';
import { Tooltip } from 'src/design-system/Tooltip';

export default function FinalizeTemplateStep(props: StepProps<TemplateCreationData>) {
  const { data, setData } = props;

  const handleTemplateNameChange = (value: string) => {
    setData({
      ...data,
      templateName: value,
    });
  };

  const handleTemplateDescriptionChange = (value: string) => {
    setData({
      ...data,
      templateDescription: value,
    });
  };

  const handleTenseChange = (value: string | null) => {
    setData({
      ...data,
      meetingNoteTense: (value as MeetingNoteTense) ?? 'THIRD_PERSON',
    });
  };

  const handleEligibleForSummarisationChange = (checked: boolean) => {
    setData(prev => ({
      ...prev,
      eligibleForSummarisation: checked,
      createdTemplate: prev.createdTemplate
        ? { ...prev.createdTemplate, eligibleForSummarisation: checked }
        : undefined,
    }));
  };

  const handleSectionSplittingLevelChange = (value: string | null) => {
    const level = value ? (Number(value) as SectionSplittingLevel) : null;
    setData(prev => ({
      ...prev,
      sectionSplittingLevel: level,
      createdTemplate: prev.createdTemplate
        ? { ...prev.createdTemplate, sectionSplittingLevel: level }
        : undefined,
    }));
  };

  const handleNextPress = async () => {
    const newData = { ...data };
    if (newData.createdTemplate) {
      newData.createdTemplate = {
        ...newData.createdTemplate,
        description: newData.templateDescription,
        displayName: newData.templateName,
      };
    }

    props.nextStep(newData);
  };

  const handleExport = () => {
    if (data.createdTemplate) {
      downloadTemplateJson(data.createdTemplate);
    }
  };

  const canGenerate = data.templateName.trim().length > 0;
  const isEditMode = !!data.createdTemplate;
  const isMeetingNote = data.templateType === 'MEETING_NOTE';

  const tenseOptions = [
    { value: 'THIRD_PERSON', label: 'Third person' },
    { value: 'FIRST_SECOND_PERSON', label: 'First & second person' },
  ];

  const sectionSplittingLevelOptions = ['1', '2', '3', '4', '5', '6'].map(value => ({
    value,
    label: value,
  }));

  const sectionSplittingLevelRequired =
    isMeetingNote && !isEditMode && data.sectionSplittingLevel == null;

  return (
    <DrawerFlowScreenLayout
      {...props}
      title="Template details"
      description="Enter a name and description for your template."
      continueText={'Continue'}
      canGoForward={
        canGenerate &&
        !!data.templateName &&
        !!data.templateDescription &&
        !sectionSplittingLevelRequired
      }
      hideProgress={true}
      nextStep={handleNextPress}
    >
      <Flex direction="column" gap="md">
        <SettingsList>
          <Flex direction="column" gap="xxxs" mb="sm">
            <Text size="textMd" fw={FONT_WEIGHTS.semibold} c="gray.10">
              Template Configuration
            </Text>
            <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.6">
              Set the name and description for your template
            </Text>
          </Flex>

          <Stack gap="sm">
            <TextInput
              label="Template name"
              placeholder="Enter template name"
              value={data.templateName}
              onChange={event => handleTemplateNameChange(event.currentTarget.value)}
              required
            />

            <Textarea
              label="Description"
              placeholder="Describe what this template is used for and when to use it"
              value={data.templateDescription || ''}
              onChange={event => handleTemplateDescriptionChange(event.currentTarget.value)}
              rows={3}
              required
            />

            {isMeetingNote && (
              <Select
                label="Writing style"
                description="Choose how pronouns are used in the meeting notes"
                data={tenseOptions}
                value={data.meetingNoteTense ?? 'THIRD_PERSON'}
                onChange={handleTenseChange}
              />
            )}

            {isMeetingNote && (
              <Select
                label={
                  <Group gap="xxxs" wrap="nowrap">
                    <Text size="sm" fw={FONT_WEIGHTS.medium}>
                      Section splitting level
                    </Text>
                    <Tooltip
                      multiline
                      w={320}
                      label="Controls how many separate pieces the AI drafts within each section when generating this document. For example, a section containing three ## sub-headings: at level 2, the AI drafts three separate pieces, one per sub-heading; at level 1, the whole section drafts as a single piece, even though it has sub-headings."
                    >
                      <IconInfoCircle size={14} style={{ cursor: 'help' }} />
                    </Tooltip>
                  </Group>
                }
                description="Choose a level to control how finely each section is divided when the AI drafts this document"
                placeholder="Select a level"
                data={sectionSplittingLevelOptions}
                value={
                  data.sectionSplittingLevel != null ? String(data.sectionSplittingLevel) : null
                }
                onChange={handleSectionSplittingLevelChange}
                error={
                  sectionSplittingLevelRequired ? 'Select a section splitting level' : undefined
                }
                required={!isEditMode}
              />
            )}

            {isMeetingNote && (
              <Switch
                label="Eligible for summarisation"
                description="When off, this template skips the summarisation step and ignores additional instructions"
                checked={data.eligibleForSummarisation ?? true}
                onChange={event =>
                  handleEligibleForSummarisationChange(event.currentTarget.checked)
                }
              />
            )}

            {isEditMode && (
              <Button
                variant="light"
                leftSection={<IconDownload size={16} />}
                onClick={handleExport}
                mt="xs"
              >
                Export to JSON
              </Button>
            )}
          </Stack>
        </SettingsList>
      </Flex>
    </DrawerFlowScreenLayout>
  );
}

type SettingsListProps = {
  children: React.ReactNode;
};

function SettingsList({ children }: SettingsListProps) {
  return (
    <Flex direction="column" bg="gray.0" style={{ borderRadius: 12, overflowX: 'hidden' }}>
      <Flex
        direction="column"
        gap="xs"
        p="md"
        bd="1px solid var(--mantine-color-gray-1)"
        style={{ borderRadius: 12, overflowX: 'hidden' }}
        bg="white"
      >
        {children}
      </Flex>
    </Flex>
  );
}
